import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { handleFailedRequest } from 'utils/failedRequestHandler';
import businessServiceAxios from 'api/businessServiceClient';
import type { PaginatedDto } from 'api/billingTypes';

export type NotificationListItemDto = {
  id: string;
  targy: string;
  szoveg?: string | null;
  letrehozasIdeje: string;
  megtekintett: boolean;
  navigaciosUrl?: string | null;
};

export type NotificationListParams = {
  pageNumber?: number;
  pageSize?: number;
};

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (params: NotificationListParams) => ['notifications', 'list', params] as const
};

function buildNotificationListUrl(params: Required<NotificationListParams>) {
  const searchParams = new URLSearchParams({
    pageNumber: params.pageNumber.toString(),
    pageSize: params.pageSize.toString(),
    isDescending: 'true'
  });

  return `/api/ertesites/ertesitesek?${searchParams.toString()}`;
}

export function useNotifications(params: NotificationListParams = {}, enabled = true) {
  const queryParams = {
    pageNumber: params.pageNumber ?? 1,
    pageSize: params.pageSize ?? 20
  };

  return useQuery({
    queryKey: notificationKeys.list(queryParams),
    queryFn: async (): Promise<PaginatedDto<NotificationListItemDto>> => {
      const response = await businessServiceAxios<{ data: PaginatedDto<NotificationListItemDto> }>(
        buildNotificationListUrl(queryParams)
      );
      return (response as unknown as { data: PaginatedDto<NotificationListItemDto> }).data;
    },
    enabled,
    refetchInterval: enabled ? 30000 : false
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string): Promise<void> => {
      await businessServiceAxios(`/api/ertesites/ertesitesek/${notificationId}`, { method: 'PATCH' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
    onError: handleFailedRequest
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<void> => {
      await businessServiceAxios('/api/ertesites/ertesitesek', { method: 'PATCH' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
    onError: handleFailedRequest
  });
}
